// ─────────────────────────────────────────────────────────────────────────────
// ReportEngine — renders a declarative report definition (from reportDefs.js)
// against a mock domain dataset (via reportingAdapter). One engine powers every
// COVID / Quality / Clinical / Risk / Sys / Admin report.
//
// A report def:
//   { domain, subtitle, auxDomains?: [...], build(rows, ctx) -> { kpis, panels } }
//   auxDomains load in parallel and arrive in ctx.aux[domain] (e.g. census for
//   rate denominators); ctx = { facilityCcn, facilityName, session, aux }.
//
//   kpis:   [{ label, value, sub?, tone? }]
//   panels: [
//     { type: "bars",  title, data: [{label,value,color?,sub?}], max?, valueFmt? },
//     { type: "table", title?, columns: [{ label, get(row), align?, strong? }], rows, empty? },
//     { type: "note",  title?, text },
//   ]
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from "react";
import { getDataset } from "./data/reportingAdapter";
import { domainNote, pccAuthHeaders } from "./data/pccAdapter";
import { ReportShell, Card, SectionTitle, KPITile, MiniBars, EmptyState } from "./reports/_shared";
import { cachedAt, PER_RESIDENT_DOMAINS, PER_ASSESSMENT_DOMAINS, SNAPSHOT_DOMAINS } from "./data/datasetCache";
import { PCC_BASE } from '../apiBase';

export default function ReportEngine({ def, title, subtitle, facilityCcn, facilityName, source, refreshKey, session }) {
  const [rows, setRows] = useState(null);
  const [aux, setAux] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setAux({});
    setError(null);
    if (!def || !def.domain) { setRows([]); return; }
    // Optional secondary domains (e.g. census for rate denominators) load in
    // parallel and arrive in ctx.aux keyed by domain; their failure is non-fatal.
    const auxDomains = def.auxDomains || [];
    Promise.all([
      getDataset(def.domain, { facilityCcn, source }),
      ...auxDomains.map((d) => getDataset(d, { facilityCcn, source }).catch(() => [])),
    ])
      .then(([main, ...auxRows]) => {
        if (cancelled) return;
        const auxMap = {};
        auxDomains.forEach((d, i) => { auxMap[d] = auxRows[i] || []; });
        setAux(auxMap);
        setRows(main);
      })
      .catch(() => { if (!cancelled) { setRows([]); setError("Could not load data for this report."); } });
    return () => { cancelled = true; };
  }, [def, facilityCcn, source, refreshKey]);

  // Optional "why is this empty?" diagnostic — a report can set def.emptyDiagnostic
  // to have the live feed report what its scan actually saw (e.g. GG: how many
  // locked MDS carry Section GG). Fetched only when the live report is empty.
  const [diag, setDiag] = useState(null);
  useEffect(() => {
    setDiag(null);
    if (!(source === "pcc" && def && def.emptyDiagnostic && Array.isArray(rows) && rows.length === 0)) return;
    let cancelled = false;
    (async () => {
      try {
        const base = PCC_BASE;   // window.PCC_PROXY_URL, else `${API_BASE}/api/pcc`
        // Fast types probe (samples residents, meta-only) so the diagnostic
        // returns quickly instead of fanning out over every resident's details.
        const diagMode = def.domain === "ggFunction" ? "types" : "1";
        const res = await fetch(`${base}?domain=${encodeURIComponent(def.domain)}&facilityCcn=${encodeURIComponent(facilityCcn)}&diag=${diagMode}`, { headers: await pccAuthHeaders() });
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled) {
          const d = (j && j.diagnostic) || null;
          // Also surface the raw assessment TYPE NAMES the feed returned — this
          // reveals functional/UDA/non-MDS assessments that the MDS-only filter
          // drops (relevant to whether daily functional data is reachable
          // without the out-of-scope ADL endpoint).
          const types = d && Array.isArray(d.assessmentTypes) && d.assessmentTypes.length
            ? ` · Assessment types returned: ${d.assessmentTypes.join(", ")}`
            : "";
          setDiag(d ? `${d.summary || ""}${types}` : null);
        }
      } catch (_) { /* diagnostic is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [source, rows, def, facilityCcn]);

  if (!def) {
    return (
      <ReportShell title={title || "Report"}>
        <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ed-muted)" }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🚧</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ed-ink)" }}>Report not available</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>This report has not been wired up yet.</div>
        </div>
      </ReportShell>
    );
  }

  if (rows === null) {
    return <ReportShell title={title}><div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ed-muted-2)" }}>Loading…</div></ReportShell>;
  }

  // Live PCC org with no rows: explain why (no sample data). Either the domain
  // isn't served by PCC (show the reason + what's needed) or it's served but
  // this facility has no records.
  if (source === "pcc" && (!rows || rows.length === 0)) {
    // Three cases, most specific first:
    //  • domainNote  → the whole domain isn't in the live feed (webhook/CMS/external).
    //  • def.emptyHint → the domain IS live, but this facility has no qualifying
    //    records for THIS report (a data-availability note, not a wiring gap).
    //  • otherwise → generic "no records for this facility".
    const note = domainNote(def.domain);
    const hint = def.emptyHint;
    // Categorize WHY it's empty so each tab says the honest thing — a wired-live
    // feed awaiting webhook data reads very differently from an external source.
    const KIND_META = {
      "awaiting-webhook": { icon: "🟢", tag: "Live — awaiting data", bg: "#ecfdf5", border: "#a7f3d0", fg: "#065f46", foot: "This report IS wired to the live PointClickCare feed — it fills automatically once the webhook events above are delivered for this facility. No further code wiring is needed." },
      "expand-scope":     { icon: "🔌", tag: "Wired & ready", bg: "var(--ed-violet-soft)", border: "var(--ed-violet-line)", fg: "#5b21b6", foot: "This report is fully built on our side and runs live in the PointClickCare sandbox today. In production it activates once this PointClickCare domain + its webhook are connected in the next validation phase (Additional Functionality Validation) — no further code work needed." },
      "external":         { icon: "🌐", tag: "External source", bg: "var(--ed-brand-soft)", border: "var(--ed-info-line)", fg: "var(--ed-info-deep)", foot: "This data does not come from PointClickCare — it's an external feed (see above). This connection is not, and is not meant to be, live through PointClickCare." },
      "cms-ccn":          { icon: "🔢", tag: "Needs a CMS CCN", bg: "var(--ed-amber-soft)", border: "var(--ed-amber-line)", fg: "var(--ed-amber-deep)", foot: "This is CMS data, wired and ready — it fills the moment the selected facility's scope value is a real 6-digit CMS certification number." },
      "platform":         { icon: "⚙️", tag: "Platform data", bg: "var(--ed-bg)", border: "var(--ed-line)", fg: "var(--ed-ink-2)", foot: "This is the app's own platform / telemetry / audit data — not PointClickCare. It fills from this app's systems, independent of the PointClickCare feed." },
    };
    const meta = note ? (KIND_META[note.kind] || { icon: "🔌", tag: "Not in the live feed", bg: "var(--ed-amber-soft)", border: "var(--ed-amber-line)", fg: "var(--ed-amber-deep)", foot: "Showing no sample data on the live org by design — this will populate once the source above is integrated." }) : null;
    const headline = meta
      ? `${meta.icon} ${note.title}`
      : hint
      ? "No qualifying records for this facility yet"
      : "No records for this facility in the live PointClickCare feed";
    const detail = note
      ? note.detail
      : hint
      ? hint
      : `${facilityName || "This facility"} returned no rows from PointClickCare for this report. If you expected data, re-check the facility or reconnect the feed (visit /api/auth/pcc/login).`;
    return (
      <ReportShell title={title} subtitle={subtitle || def.subtitle}>
        <div className="card" style={{ padding: 24, background: meta ? meta.bg : "var(--ed-amber-soft)", border: `1px solid ${meta ? meta.border : "#fde68a"}`, display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{ fontSize: 22 }}>{note ? "" : "📭"}</div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "var(--ed-ink)" }}>{headline}</div>
              {meta && <span style={{ fontSize: 10.5, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: meta.fg, color: "var(--ed-canvas)" }}>{meta.tag}</span>}
            </div>
            <div style={{ fontSize: 13.5, color: meta ? meta.fg : "var(--ed-amber-deep)", lineHeight: 1.6, marginTop: 6 }}>{detail}</div>
            <div style={{ fontSize: 12, color: meta ? meta.fg : "#a16207", marginTop: 8, opacity: 0.9 }}>
              {meta
                ? meta.foot
                : "This is a data-availability state on the live feed, not a wiring issue — the report will fill as soon as the underlying records exist for this facility."}
            </div>
            {def.emptyDiagnostic && (
              <div style={{ fontSize: 12, color: "var(--ed-ink-2)", marginTop: 10, padding: "8px 11px", background: "#fef9ec", border: "1px dashed #e7c76b", borderRadius: 8, fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}>
                <span style={{ fontWeight: 700 }}>Live diagnostic:</span> {diag || "checking the feed…"}
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    );
  }

  let result;
  try {
    result = def.build(rows, { facilityCcn, facilityName, session, aux }) || {};
  } catch (e) {
    return (
      <ReportShell title={title} subtitle={subtitle || def.subtitle}>
        <div className="card" style={{ padding: 24, color: "var(--ed-alert-deep)", background: "var(--ed-alert-soft)", border: "1px solid var(--ed-alert-line)" }}>
          This report hit an error while computing. {String(e && e.message ? e.message : e)}
        </div>
      </ReportShell>
    );
  }

  const { kpis = [], panels = [] } = result;
  const barPanels = panels.filter((p) => p.type === "bars");
  const otherPanels = panels.filter((p) => p.type !== "bars");

  return (
    <ReportShell title={title} subtitle={subtitle || def.subtitle}>
      {error && <div className="card" style={{ padding: 14, marginBottom: 14, color: "var(--ed-alert-deep)", background: "var(--ed-alert-soft)", border: "1px solid var(--ed-alert-line)" }}>{error}</div>}
      {/* WHERE THESE NUMBERS CAME FROM AND WHEN.
          "Is this report actually being fed?" had no answer on screen — a
          populated report and a stale one looked identical, and so did live
          data and sample data. It carries into the printed copy too. */}
      <Provenance domain={def.domain} facilityCcn={facilityCcn} source={source} rowCount={rows.length} />

      {kpis.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
          {kpis.map((k, i) => <KPITile key={i} label={k.label} value={k.value} sub={k.sub} tone={k.tone} />)}
        </div>
      )}

      {barPanels.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16, marginBottom: otherPanels.length ? 16 : 0 }}>
          {barPanels.map((p, i) => (
            <Card key={i}>
              {p.title && <SectionTitle>{p.title}</SectionTitle>}
              <MiniBars data={p.data} max={p.max} valueFmt={p.valueFmt} />
            </Card>
          ))}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {otherPanels.map((p, i) => {
          if (p.type === "note") {
            return (
              <Card key={i}>
                {p.title && <SectionTitle>{p.title}</SectionTitle>}
                <div style={{ fontSize: 13.5, color: "var(--ed-ink-2)", lineHeight: 1.6 }}>{p.text}</div>
              </Card>
            );
          }
          // table
          return <DataTable key={i} title={p.title} columns={p.columns} rows={p.rows} empty={p.empty} />;
        })}
      </div>
    </ReportShell>
  );
}

// ── Provenance ───────────────────────────────────────────────────────────────
//
// One line saying what fed this report, when, and what a refresh would cost.
// Three facts a person needed and could not get:
//
//   Is it live or sample?     A populated sample report and a populated live
//                             one looked identical, and the sample one can
//                             walk out of the building in a printout.
//   How old is it?            Held for the session now, so "when" is a real
//                             question with a real answer.
//   What does Refresh cost?   Per-resident domains are the roster plus one
//                             PointClickCare call for every resident on it.
//                             Say so before the button, not after.
function Provenance({ domain, facilityCcn, source, rowCount }) {
  const at = cachedAt(domain, facilityCcn, source);
  const live = source === "pcc";
  const snapshot = live && SNAPSHOT_DOMAINS.has(domain);
  const perResident = live && PER_RESIDENT_DOMAINS.has(domain) && !snapshot;
  const perAssessment = perResident && PER_ASSESSMENT_DOMAINS.has(domain);

  const when = (() => {
    if (!at) return null;
    const mins = Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  })();

  const feed = !live
    ? { label: "Sample data", color: "var(--ed-amber-deep)", bg: "var(--ed-amber-soft)", border: "var(--ed-amber-line)" }
    : snapshot
      ? { label: "Stored webhook snapshot", color: "#065f46", bg: "#ecfdf5", border: "#a7f3d0" }
      : { label: "Live PointClickCare", color: "var(--ed-brand)", bg: "var(--ed-brand-soft)", border: "var(--ed-info-line)" };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", marginBottom: 14, fontSize: 11.5, color: "var(--ed-muted)" }}>
      <span className="badge" style={{ color: feed.color, background: feed.bg, border: `1px solid ${feed.border}` }}>{feed.label}</span>
      <span>{rowCount} row{rowCount === 1 ? "" : "s"}</span>
      {when && <span>· fetched {when}, held for this session</span>}
      {snapshot && <span>· read from stored events, no PointClickCare call</span>}
      {perResident && (
        <span title={perAssessment
          ? "The roster, then one PointClickCare call per resident for their assessment list, then one more for every assessment's detail."
          : "The roster, then one PointClickCare call for every resident on it."}>
          · ↻ Refresh re-pulls {perAssessment ? "per resident and per assessment" : "per resident"} — use sparingly
        </span>
      )}
    </div>
  );
}

function DataTable({ title, columns = [], rows = [], empty }) {
  if (!rows || rows.length === 0) {
    return (
      <div>
        {title && <SectionTitle>{title}</SectionTitle>}
        <EmptyState>{empty || "No rows for this scope."}</EmptyState>
      </div>
    );
  }
  return (
    <div>
      {title && <SectionTitle>{title}</SectionTitle>}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--ed-bg)", borderBottom: "1px solid var(--ed-line)" }}>
              {columns.map((c, i) => (
                <th key={i} style={{ textAlign: c.align || "left", padding: "10px 12px", fontSize: 11, fontWeight: 700, color: "var(--ed-muted)", textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="table-row" style={{ borderBottom: "1px solid var(--ed-line-2)" }}>
                {columns.map((c, ci) => (
                  <td key={ci} style={{ padding: "9px 12px", textAlign: c.align || "left", fontWeight: c.strong ? 700 : 400, color: c.strong ? "var(--ed-ink)" : "var(--ed-ink-2)", whiteSpace: c.wrap ? "normal" : "nowrap" }}>
                    {c.get(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
